import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import ProjectInsuranceMigration from './ProjectInsuranceMigration';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const money = (value) => Number(value || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

const ProjectInsurance = () => {
  const { projectVuid } = useParams();
  const location = useLocation();
  const inPreview = location.pathname.startsWith('/procore/');
  const calculationsPath = inPreview
    ? `/procore/projects/${projectVuid}/calculations`
    : `/projects/${projectVuid}/calculations`;
  const libraryPath = inPreview ? '/procore/setup/calculation-library' : '/calculation-library';

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [migration, setMigration] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${baseURL}/api/projects/${projectVuid}/gl-insurance`),
      axios.get(`${baseURL}/api/projects/${projectVuid}/gl-insurance/history`),
      axios.get(`${baseURL}/api/projects/${projectVuid}/gl-insurance/migration`),
    ]).then(([settingsResponse, historyResponse, migrationResponse]) => {
      if (cancelled) return;
      setSettings(settingsResponse.data);
      setHistory(historyResponse.data?.history || []);
      setMigration(migrationResponse.data);
    }).catch((err) => {
      if (!cancelled) setError(err.response?.data?.error || 'Could not load GL Insurance.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  if (loading) return <div className="py-8 text-center text-gray-600">Loading GL Insurance...</div>;
  if (error) return <div className="py-8 text-center text-red-700">{error}</div>;

  const lines = settings?.lines || [];
  const destinationCount = lines.length;
  const sourceCount = lines.reduce((sum, line) => sum + ((line.assignments || []).length), 0);
  const lastPosted = history[0];
  const moved = migration?.active;

  if (!settings?.vuid) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold text-gray-900">GL Insurance</h1>
        <p className="text-sm text-gray-700 mt-3">
          GL Insurance is a Project Calculation. This project does not have a legacy setup.
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          <Link to={calculationsPath} className="btn-claret inline-flex items-center">Open Project Calculations</Link>
          <Link to={libraryPath} className="btn-claret inline-flex items-center">Calculation Library</Link>
        </div>
      </div>
    );
  }

  const journalPath = (periodVuid) => (
    inPreview
      ? `/procore/projects/${projectVuid}/reports/journal-entries`
      : `/journal-entries?accounting_period_vuid=${periodVuid}`
  );

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">GL Insurance</h1>
        {moved ? (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mt-4">Moved to Project Calculations</h2>
            <p className="text-sm text-gray-700 mt-1">
              Effective {moved.cutover_label}. Historical GL Insurance stays on this page.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mt-4">Historical GL Insurance</h2>
            <p className="text-sm text-gray-700 mt-1">
              New insurance is a Project Calculation. This page keeps the old setup and any periods already posted.
            </p>
          </>
        )}
        <div className="mt-4">
          <Link to={calculationsPath} className="btn-claret inline-flex items-center">Open Project Calculations</Link>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow p-5">
        <h2 className="text-base font-semibold text-gray-900">Legacy setup</h2>
        <p className="text-sm text-gray-600 mt-1">Read only. Future rates and destinations are changed on the Project Calculation.</p>
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mt-4">
          <div>
            <dt className="text-gray-500">Status</dt>
            <dd className="font-medium text-gray-900">{settings.enabled ? 'Enabled' : 'Disabled'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Insurance rate</dt>
            <dd className="font-medium text-gray-900">${settings.insurance_rate} / $1,000</dd>
          </div>
          <div>
            <dt className="text-gray-500">Accrual rate</dt>
            <dd className="font-medium text-gray-900">${settings.insurance_accrual_rate} / $1,000</dd>
          </div>
          <div>
            <dt className="text-gray-500">Treatment</dt>
            <dd className="font-medium text-gray-900">{settings.accounting_treatment === 'create_project_cost' ? 'Create Project Cost' : 'Journal Only'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Destinations</dt>
            <dd className="font-medium text-gray-900">{destinationCount}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Source pairs</dt>
            <dd className="font-medium text-gray-900">{sourceCount}</dd>
          </div>
          <div className="md:col-span-3">
            <dt className="text-gray-500">Last legacy posted period</dt>
            <dd className="font-medium text-gray-900">{lastPosted ? lastPosted.period_label : 'None'}</dd>
          </div>
        </dl>
      </div>
      <ProjectInsuranceMigration
        projectVuid={projectVuid}
        inPreview={inPreview}
        onMigration={setMigration}
      />
      <div className="bg-white rounded-xl shadow p-5">
        <h2 className="text-base font-semibold text-gray-900">Legacy history</h2>
        {!history.length ? (
          <p className="text-sm text-gray-600 mt-2">No legacy GL Insurance has been posted for this project.</p>
        ) : (
          <div className="mt-3 space-y-4">
            {history.map((row) => (
              <div key={row.vuid} className="border border-gray-200 rounded-lg p-4 text-sm">
                <div className="font-semibold text-gray-900">{row.period_label}</div>
                <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                  <div><dt className="text-gray-500">Eligible billing</dt><dd>{money(row.eligible_amount)}</dd></div>
                  <div><dt className="text-gray-500">Insurance rate</dt><dd>${row.insurance_rate} / $1,000</dd></div>
                  <div><dt className="text-gray-500">Accrual rate</dt><dd>${row.insurance_accrual_rate} / $1,000</dd></div>
                  <div><dt className="text-gray-500">Premium</dt><dd>{money(row.premium)}</dd></div>
                  <div><dt className="text-gray-500">Accrual</dt><dd>{money(row.accrual)}</dd></div>
                  <div><dt className="text-gray-500">Recovery</dt><dd>{money(row.recovery)}</dd></div>
                  <div><dt className="text-gray-500">Treatment</dt><dd>{row.treatment_label}</dd></div>
                  <div><dt className="text-gray-500">Destination</dt><dd>{(row.destinations || []).map((item) => item.label).join(', ') || '—'}</dd></div>
                  <div>
                    <dt className="text-gray-500">Journal</dt>
                    <dd>
                      {row.journal_reference ? (
                        <Link className="text-blue-800 hover:underline" to={journalPath(row.accounting_period_vuid)}>
                          {row.journal_reference}
                        </Link>
                      ) : '—'}
                    </dd>
                  </div>
                  <div><dt className="text-gray-500">Project cost</dt><dd>{row.project_cost ? 'Yes' : 'No'}</dd></div>
                  <div><dt className="text-gray-500">Posted by</dt><dd>{row.posted_by || '—'}</dd></div>
                  <div><dt className="text-gray-500">Posted</dt><dd>{row.posted_at ? String(row.posted_at).slice(0, 10) : '—'}</dd></div>
                </dl>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectInsurance;
